import {html, HTMLTemplateResult, PropertyValues} from "lit";
import {AbstractO2dElement} from "@o2d/shared";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {createRef, ref} from "lit/directives/ref.js";
import {GridStack, type GridItemHTMLElement, type GridStackNode} from "gridstack";
import "gridstack/dist/gridstack.css";
import {container} from "tsyringe";

import {CommandBus} from "../../services/command-bus.service";
import {UpdatePersonalLabelCommand} from "../../commands/update-personal-label.command";
import {UiHelper} from "../../helpers/ui.helper";
import type {PersonalLabel} from "../../domain/entities/personal-label.entity";
import "./my-organizer-label-column.component";
import {MyOrganizerLabelColumnComponent} from "./my-organizer-label-column.component";
import {
    MY_ORGANIZER_LABEL_DELETED_EVENT,
    type MyOrganizerLabelDeletedEventDetail,
} from "./my-organizer-drag-context";

/** Gridstack's own units: grid-cell columns/rows, not pixels — same shape
 *  `LinkTile` uses for the Linkview grid (`dashboard-creator.component.ts`). */
const GRID_COLUMNS = 12;
const CELL_HEIGHT_PX = 90;
/** ~3 boxes per row on a 12-column grid, roughly matching today's equal-width
 *  flex columns as the starting layout for a box with no saved size yet. */
const DEFAULT_BOX_WIDTH = 4;
/** Tall enough to show a handful of tickets before the box's own internal
 *  scroll (`.my-organizer-label-column__body { overflow-y: scroll }`) takes
 *  over. A starting guess (2026-09-13) — easy to retune visually, unlike the
 *  data model / persistence wiring around it. */
const DEFAULT_BOX_HEIGHT = 5;

/**
 * "My Organizer" mode's free-grid container for the personal-label "Boxen"
 * (Anke, 2026-09-10/13: "die Boxen ... beliebig im Grid verschiebbar und
 * skalierbar", scope-decided 2026-09-13: position AND size, per-User,
 * autosaved) — replaces `o2d-personal-organizer.view.ts`'s previous fixed
 * `repeat()`-rendered flex row of `<my-organizer-label-column>` elements.
 *
 * Built on `gridstack` (already a dependency, `^13.2.0`), the SAME package
 * `dashboard-creator.component.ts` uses for the Linkview grid — same
 * `GridStack.init()` shape (float/cellHeight/column/columnOpts), same
 * "Gridstack owns `.grid-stack`'s DOM imperatively, Lit's `repeat()` must
 * never also render into it" reasoning (the two would fight over the same
 * nodes), same `load()`-then-mount-by-`[gs-id]` pattern (`_mountAll()`).
 *
 * Deliberately DIFFERENT from Linkview in two ways, both because My
 * Organizer boxes are per-User rather than a shared board:
 * 1. AUTOSAVE on every drag/resize (`_onGridChange()`), not an explicit
 *    "Speichern" button — Linkview's button exists specifically so
 *    "dragging a tile around while thinking must not publish half-finished
 *    states to everybody" (that component's own doc comment); there is no
 *    "everybody" here, and the rest of My Organizer already persists
 *    implicitly (ticket drag-reorder, inline rename).
 * 2. No shared-board lock mechanism (`LockEntityCommand`/etc.) — boxes are
 *    already scoped to their owner (`PersonalLabelRepository.findAllMine()`),
 *    Linkview's locking has nothing to protect against here.
 *
 * Persists via the EXISTING per-entity `ChangeTracker` + `UpdatePersonalLabelCommand`
 * flow every other `PersonalLabel` mutation already uses (see
 * `my-organizer-label-column.component.ts`'s own rename flow) — one command
 * per box that Gridstack's `change` event actually reports as moved/resized,
 * NOT Linkview's bulk `diffTiles`/reorder-command pattern (that machinery
 * exists to make "one drag reflows many tiles" a single transaction, which
 * matters less for independent per-User boxes).
 *
 * A box's `x`/`y`/`w`/`h` of `null` means "no position saved yet" (see the
 * entity's own doc comment) — `_mountAll()` passes `undefined` for those to
 * Gridstack, which auto-places them via its own first-fit packing in
 * ARRAY ORDER, i.e. `this.labels`' existing creation order — reproducing
 * today's left-to-right layout for anyone who has never dragged anything,
 * without this component needing its own placement algorithm.
 *
 * Drag is scoped to the box's header (`handle:
 * '.my-organizer-label-column__header'`) so grabbing the box does not
 * fight with each box's OWN native HTML5 drag & drop for tickets
 * (`draggable="true"` rows in `my-organizer-label-column.component.ts`) —
 * a different mechanism from Gridstack's own pointer-based dragging, but
 * still worth scoping so "grab anywhere on the box" doesn't shadow "grab a
 * ticket". Gridstack's default `cancel` selector
 * (`'input,textarea,button,select,option'`) already keeps the header's own
 * rename `<input>` and delete `<button>` from starting a box-drag, with no
 * extra configuration needed.
 */
@customElement('my-organizer-boxes-grid')
export class MyOrganizerBoxesGridComponent extends AbstractO2dElement {

    @property({attribute: false})
    public labels: PersonalLabel[] = [];

    /** @internal Public only so unit tests can hand in a stub grid container. */
    public _gridElRef = createRef<HTMLDivElement>();
    private _grid: GridStack | null = null;

    /** The mounted `<my-organizer-label-column>` per label uid — lets
     *  `_mountColumn()` recognise "already mounted for this exact label
     *  instance" and skip re-mounting (which would otherwise discard the
     *  column's own live state: open rename input, loaded rows, ...) on
     *  every unrelated `labels` array change (a sibling box created/deleted). */
    private readonly _columnElements = new Map<string, MyOrganizerLabelColumnComponent>();

    /**
     * Set for the entire duration of a programmatic `_grid.load()` call —
     * Gridstack fires 'change' synchronously and Gridstack's own auto-
     * placement of a not-yet-positioned box (or a reflow from adding/
     * removing a sibling) would otherwise look INDISTINGUISHABLE from a real
     * user drag/resize to `_onGridChange()`, persisting a position the User
     * never actually chose. `load()` itself is fully synchronous (wrapped in
     * `batchUpdate()`, which defers every event to the very end of the
     * call), so a plain boolean set immediately before and cleared
     * immediately after is sufficient — no async window to race.
     *
     * @internal Public only so unit tests can observe it.
     */
    public _applyingLayout = false;

    /**
     * Per-box save queue (Review-03, 2026-09-13, MAJOR-Fund — replaces an
     * earlier `_persistGeneration` counter from Review-02). That counter
     * caught "an older failed save must not roll back a newer succeeded
     * one", but counted PER BOX rather than per attempt: an unrelated SECOND
     * change to the same box (e.g. a resize while a drag's own save is
     * still in flight) bumped the same counter, so a later genuine failure
     * of the FIRST save saw a "newer generation exists" mismatch and
     * silently skipped BOTH its rollback and its error toast — even though
     * nothing had actually superseded ITS OWN field. `label.x` (say) then
     * permanently diverged from the backend with no visible sign at all.
     *
     * A queue removes the race STRUCTURALLY instead of detecting it after
     * the fact: `_queuePersist()` chains each new save for a box onto
     * whatever is already pending for that SAME `uid`, so saves for one box
     * always run strictly one-at-a-time — by the time a second save for a
     * box starts, the first has already fully finished (success, or
     * failure+rollback+toast). Different boxes still save fully
     * concurrently; only same-box overlap is serialised.
     */
    private readonly _pendingPersist = new Map<string, Promise<void>>();

    public override firstUpdated(): void {
        const gridEl = this._gridElRef.value;
        if (!gridEl) return;

        this._grid = GridStack.init({
            float: true,
            margin: 8,
            cellHeight: CELL_HEIGHT_PX,
            column: GRID_COLUMNS,
            handle: '.my-organizer-label-column__header',
            columnOpts: {
                breakpoints: [
                    {w: 1200, c: 6},
                    {w: 768, c: 3},
                    {w: 480, c: 1},
                ],
            },
        }, gridEl);

        // Non-null: GridStack.init() returns null only without a `document` or without an
        // element, and `gridEl` was checked above.
        this._grid!.on('change', (_event, nodes) => this._onGridChange(nodes));

        // Review-01, 2026-09-13 (MINOR-Fund): NOT mounted here — Lit calls
        // `updated()` right after `firstUpdated()` in this SAME first update
        // cycle (with the same `changedProperties`), and `labels` is always
        // "changed" on that very first cycle (a fresh `[]` default vs.
        // whatever the caller bound — different references even when both
        // are empty). `updated()` below already handles that first cycle,
        // so mounting here too would run `_mountAll()`/`grid.load()` twice
        // on every mount — harmless today (see that method's own guards),
        // but a fragile lifecycle assumption for anything touching those
        // guards later.
    }

    public override updated(changed: PropertyValues): void {
        if (changed.has('labels') && this._grid) {
            this._mountAll(this.labels);
        }
    }

    public override disconnectedCallback(): void {
        this._grid?.destroy(false);
        this._grid = null;
        super.disconnectedCallback();
    }

    /**
     * Puts the whole label list onto the grid — mirrors `dashboard-creator
     * .component.ts`'s own `_mountAll()` (see its doc comment for why
     * `load()`, not a loop of `addWidget()`, is required for a correct
     * `save()`/reflow cache): one `load()` call, then one mount per label by
     * `[gs-id]` lookup. `addRemove` defaults to true, so a label no longer
     * in the list is removed from the grid — what makes this safe to call
     * again after ANY change to `labels` (add, delete, or a fresh read),
     * not only on first mount.
     */
    private _mountAll(labels: PersonalLabel[]): void {
        if (!this._grid) return;

        this._applyingLayout = true;
        try {
            this._grid.load(labels.map(label => ({
                id: label.uid,
                x: label.x ?? undefined,
                y: label.y ?? undefined,
                w: label.w ?? DEFAULT_BOX_WIDTH,
                h: label.h ?? DEFAULT_BOX_HEIGHT,
            })));
        } finally {
            this._applyingLayout = false;
        }

        for (const label of labels) {
            const el = this._gridElRef.value?.querySelector<GridItemHTMLElement>(`[gs-id="${label.uid}"]`);
            if (el) this._mountColumn(el, label);
        }
    }

    private _mountColumn(gridItemEl: GridItemHTMLElement, label: PersonalLabel): void {
        const host = gridItemEl.querySelector<HTMLElement>('.grid-stack-item-content');
        if (!host) return;

        // Already mounted for this EXACT label instance (see the field's own
        // doc comment) — leave its live internal state untouched.
        const existing = this._columnElements.get(label.uid);
        if (existing?.label === label) return;

        const el = new MyOrganizerLabelColumnComponent();
        el.label = label;
        // Relay upward exactly like the removed Lit template binding did
        // (`@my-organizer.label-deleted=` on each `<my-organizer-label-column>`
        // in `o2d-personal-organizer.view.ts`) — that binding now sits on
        // THIS component instead, since the view no longer renders the
        // columns itself. `MY_ORGANIZER_LABEL_DELETED_EVENT` does not bubble
        // (`dispatchEvent(new CustomEvent(...))` with no `bubbles: true` in
        // the column's own dispatch), so it must be re-dispatched explicitly,
        // not merely left to propagate.
        el.addEventListener(MY_ORGANIZER_LABEL_DELETED_EVENT, (event) => {
            this._columnElements.delete(label.uid);
            // Review-03, 2026-09-13 (MINOR-Fund): without this, `_pendingPersist`
            // keeps a settled-Promise entry for every `uid` that ever had a
            // save queued, for the lifetime of the component — a small
            // memory leak over a long session with many boxes created and
            // deleted. A currently IN-FLIGHT save for this box is left to
            // finish on its own (its own failure branch separately checks
            // whether the box still exists before showing a toast — see
            // `_persistGeometry()`'s own comment); this only drops the
            // now-stale queue ENTRY so the next `uid` reuse (never happens
            // in practice, uids are UUIDs, but conceptually) starts clean.
            this._pendingPersist.delete(label.uid);
            this.dispatchEvent(new CustomEvent<MyOrganizerLabelDeletedEventDetail>(
                MY_ORGANIZER_LABEL_DELETED_EVENT,
                {detail: (event as CustomEvent<MyOrganizerLabelDeletedEventDetail>).detail},
            ));
        });

        this._columnElements.set(label.uid, el);
        host.replaceChildren(el);

        // Anke, 2026-09-13 (bug report): "wenn ich ein Ticket in eine andere
        // Box ziehen möchte, dann wird die ganze Box gezogen" — Gridstack
        // scans for the `handle` selector SYNCHRONOUSLY while preparing a
        // widget's drag/drop (`makeWidget()`, called from `load()` above,
        // BEFORE this method even runs). At that moment `.grid-stack-item-
        // content` is still empty — `<my-organizer-label-column>` is a Lit
        // element that renders its own `.my-organizer-label-column__header`
        // asynchronously, on its own first update, not synchronously when
        // constructed/appended. Finding no match, Gridstack falls back to
        // treating the WHOLE item as the drag surface — exactly the
        // reported bug. `updateComplete` resolves once that first render has
        // actually happened; `refreshDragHandles()` is Gridstack's own
        // documented fix for "a custom handle selector that lives inside
        // dynamically-created content" (its own doc comment names React
        // portals/Angular components as the usual case — a Lit light-DOM
        // custom element hits the exact same timing gap).
        void el.updateComplete.then(() => this._grid?.refreshDragHandles(gridItemEl));
    }

    /** @internal Public only so unit tests can feed Gridstack change events. */
    public _onGridChange(nodes: GridStackNode[]): void {
        if (this._applyingLayout) return;

        for (const node of nodes) {
            if (!node.id) continue;
            const label = this.labels.find(l => l.uid === node.id);
            if (!label) continue;
            this._queuePersist(label, node);
        }
    }

    /**
     * Chains this save onto whatever is already pending for this SAME box
     * — see `_pendingPersist`'s own doc comment for why (Review-03,
     * 2026-09-13, MAJOR-Fund fix). `.catch(() => {})` on the predecessor:
     * `_persistGeometry()` itself never rejects (its one `await` is a
     * `dispatchCommand()` call whose failure is a `false` return, not a
     * throw), but a broken chain here would be a silent, hard-to-diagnose
     * "saves for this box stopped working" bug if that ever changed —
     * cheap insurance, not defending against anything currently reachable.
     */
    private _queuePersist(label: PersonalLabel, node: GridStackNode): void {
        const previous = this._pendingPersist.get(label.uid) ?? Promise.resolve();
        const next = previous.catch(() => {}).then(() => this._persistGeometry(label, node));
        this._pendingPersist.set(label.uid, next);
    }

    /**
     * Mutates the live `PersonalLabel` proxy and dispatches — mirrors
     * `my-organizer-label-column.component.ts`'s own rename flow
     * (`_saveLabelRename()`): `ChangeTracker` reports only the fields that
     * actually differ as the command payload, so only genuinely changed
     * axes reach the backend even though all four are always assigned here.
     * `changedAny` guards the dispatch itself — a `change` event that ends
     * up reporting the box's CURRENT values (Gridstack can fire one for a
     * neighbour reflow without this box's own geometry actually moving)
     * must not still send an empty no-op command.
     *
     * Review-01, 2026-09-13 (WARNING-Fund): unlike the rename flow this
     * mirrors, a failed save here is rolled back not just in the ENTITY
     * (`label.x/y/w/h` back to their pre-drag values) but also VISUALLY —
     * `_saveLabelRename()`'s `requestUpdate()` is enough for a Lit-rendered
     * text property, but Gridstack owns this widget's on-screen position
     * imperatively, entirely outside Lit's own re-render — resetting only
     * the entity would leave the box LOOKING like it kept its new spot
     * (silent-looking data loss) until the next full reload. `_grid.update()`
     * itself fires ANOTHER 'change' event; `_applyingLayout` (the same flag
     * `_mountAll()` uses) suppresses that so the rollback doesn't try to
     * persist itself right back.
     *
     * Review-03, 2026-09-13 (MAJOR-Fund): this is now ALWAYS the only
     * in-flight save for this box — `_queuePersist()` serialises same-box
     * saves, so there is no "did a newer attempt supersede me" question
     * left to ask here at all (an earlier per-box generation counter tried
     * to answer that question after the fact and got it wrong for
     * unrelated-field changes; the queue removes the question itself).
     *
     * Also checks the box still exists in `this.labels` before showing the
     * failure toast (Review-03, 2026-09-13, MINOR-Fund) — a box deleted
     * WHILE its save was in flight has nothing left for the User to be
     * told "could not be saved" about; `_snapBackVisually()` already
     * degrades gracefully for this case (no grid item to find), but the
     * toast has no such natural no-op and needs its own check.
     */
    private async _persistGeometry(label: PersonalLabel, node: GridStackNode): Promise<void> {
        const previous = {x: label.x, y: label.y, w: label.w, h: label.h};
        let changedAny = false;
        if (node.x !== undefined && label.x !== node.x) {
            label.x = node.x;
            changedAny = true;
        }
        if (node.y !== undefined && label.y !== node.y) {
            label.y = node.y;
            changedAny = true;
        }
        if (node.w !== undefined && label.w !== node.w) {
            label.w = node.w;
            changedAny = true;
        }
        if (node.h !== undefined && label.h !== node.h) {
            label.h = node.h;
            changedAny = true;
        }
        if (!changedAny) return;

        const dispatched = await container.resolve(CommandBus).dispatchCommand(
            new UpdatePersonalLabelCommand('personal-label', label.uid, label)
        );

        if (!dispatched) {
            console.error('[my-organizer-boxes-grid] failed to save a box position/size change');

            label.x = previous.x;
            label.y = previous.y;
            label.w = previous.w;
            label.h = previous.h;
            this._snapBackVisually(label, previous);

            if (this.labels.includes(label)) {
                UiHelper.notify({
                    level: 'error',
                    title: 'Fehler',
                    content: html`<p>Die neue Position/Größe der Box konnte nicht gespeichert werden.</p>`,
                    closable: true,
                });
            }
        }
    }

    /**
     * Moves the Gridstack widget back to its pre-drag position/size after a
     * failed save — `label.x/y/w/h` alone drives nothing visual (Gridstack
     * positions its DOM node imperatively, not through Lit's own render).
     * `_applyingLayout` suppresses the 'change' event this itself provokes,
     * for the same reason `_mountAll()` raises it around `grid.load()`.
     */
    private _snapBackVisually(label: PersonalLabel, previous: {x: number | null; y: number | null; w: number | null; h: number | null}): void {
        const el = this._gridElRef.value?.querySelector<GridItemHTMLElement>(`[gs-id="${label.uid}"]`);
        if (!this._grid || !el) return;

        this._applyingLayout = true;
        try {
            this._grid.update(el, {
                x: previous.x ?? undefined,
                y: previous.y ?? undefined,
                w: previous.w ?? undefined,
                h: previous.h ?? undefined,
            });
        } finally {
            this._applyingLayout = false;
        }
    }

    public render(): HTMLTemplateResult {
        return html`
            <div class="my-organizer-boxes-grid grid-stack" ${ref(this._gridElRef)}></div>
        `;
    }

}
